import { useEffect, type JSX } from 'react';
import type { Control, UseFormSetValue } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import {
  CustomFieldsBuilder,
  type CustomFieldFormRow,
} from '@/components/machine-types/CustomFieldsBuilder';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useZodForm } from '@/lib/forms/useZodForm';
import type { CustomField } from '@/types/machine-types';

function fromCustomField(cf: CustomField): CustomFieldFormRow {
  return {
    key: cf.key,
    label: cf.label,
    dataType: cf.dataType,
    required: cf.required,
    dropdownOptions: cf.dropdownOptions === null ? null : [...cf.dropdownOptions],
  };
}

function toCustomField(row: CustomFieldFormRow): CustomField {
  return {
    key: row.key,
    label: row.label,
    dataType: row.dataType,
    required: row.required,
    dropdownOptions: row.dropdownOptions,
  };
}

/**
 * Plan 10-02 D-95 — MachineTypeForm
 *
 * Shared between create and edit modes (via `mode` prop). Schema mirrors the
 * Plan 10-01 backend validation:
 *  - name: 3..100 chars
 *  - description: nullable, ≤1000 chars
 *  - customFields[]:
 *      - key: 1..50 chars, regex `^[a-zA-Z][a-zA-Z0-9_]*$`, unique within type
 *      - label: 1..100 chars
 *      - dataType: 'text' | 'number' | 'date' | 'dropdown'
 *      - required: boolean
 *      - dropdownOptions: required + non-empty when dataType==='dropdown',
 *        null otherwise (cross-field rule via superRefine)
 *  - isActive: boolean
 *
 * Cross-row duplicate-key detection is enforced both via:
 *  (a) Zod array-level superRefine (fires on submit), and
 *  (b) inline rendering inside CustomFieldsBuilder (fires on every keystroke).
 */

const KEY_REGEX = /^[a-zA-Z][a-zA-Z0-9_]*$/;

const customFieldSchema = z
  .object({
    key: z
      .string()
      .min(1, 'Required')
      .max(50, 'At most 50 characters')
      .regex(
        KEY_REGEX,
        'Key must start with a letter and contain only letters, digits, and underscores',
      ),
    label: z.string().min(1, 'Required').max(100, 'At most 100 characters'),
    dataType: z.enum(['text', 'number', 'date', 'dropdown']),
    required: z.boolean(),
    dropdownOptions: z.array(z.string().min(1, 'Option cannot be empty')).nullable(),
  })
  .superRefine((field, ctx) => {
    if (
      field.dataType === 'dropdown' &&
      (field.dropdownOptions === null || field.dropdownOptions.length === 0)
    ) {
      ctx.addIssue({
        path: ['dropdownOptions'],
        code: 'custom',
        message: 'Dropdown fields require at least one option',
      });
    } else if (
      field.dataType !== 'dropdown' &&
      field.dropdownOptions !== null &&
      field.dropdownOptions.length > 0
    ) {
      ctx.addIssue({
        path: ['dropdownOptions'],
        code: 'custom',
        message: 'Non-dropdown fields must not have options',
      });
    }
  });

const machineTypeFormSchema = z
  .object({
    name: z.string().min(3, 'At least 3 characters').max(100, 'At most 100 characters'),
    description: z
      .string()
      .max(1000, 'At most 1000 characters')
      .nullable()
      .or(z.literal('').transform(() => null)),
    customFields: z.array(customFieldSchema).superRefine((fields, ctx) => {
      const seen = new Set<string>();
      fields.forEach((f, idx) => {
        if (f.key.length > 0 && seen.has(f.key)) {
          ctx.addIssue({
            path: [idx, 'key'],
            code: 'custom',
            message: `Key '${f.key}' is duplicated`,
          });
        }
        seen.add(f.key);
      });
    }),
    isActive: z.boolean(),
  })
  .strict();

/** Internal form shape (mutable arrays for RHF). The exported public-facing
 * shape uses readonly arrays via `MachineTypeFormValues`. */
type FormShape = z.infer<typeof machineTypeFormSchema>;

/** Public-facing form values — readonly arrays for the wire DTO. */
export interface MachineTypeFormValues {
  readonly name: string;
  readonly description: string | null;
  readonly customFields: readonly CustomField[];
  readonly isActive: boolean;
}

export interface MachineTypeFormProps {
  readonly mode: 'create' | 'edit';
  readonly initialValues?: {
    readonly name?: string;
    readonly description?: string | null;
    readonly customFields?: readonly CustomField[];
    readonly isActive?: boolean;
  };
  readonly onSubmit: (values: MachineTypeFormValues) => void | Promise<void>;
  readonly submitting?: boolean;
  readonly errorMessage?: string | null;
}

const EMPTY_DEFAULTS: FormShape = {
  name: '',
  description: null,
  customFields: [],
  isActive: true,
};

function buildDefaults(initialValues: MachineTypeFormProps['initialValues']): FormShape {
  return {
    name: initialValues?.name ?? EMPTY_DEFAULTS.name,
    description: initialValues?.description ?? EMPTY_DEFAULTS.description,
    customFields:
      initialValues?.customFields !== undefined
        ? initialValues.customFields.map(fromCustomField)
        : [],
    isActive: initialValues?.isActive ?? EMPTY_DEFAULTS.isActive,
  };
}

export function MachineTypeForm({
  mode,
  initialValues,
  onSubmit,
  submitting = false,
  errorMessage,
}: MachineTypeFormProps): JSX.Element {
  const defaults: FormShape = buildDefaults(initialValues);

  const form = useZodForm({
    schema: machineTypeFormSchema,
    defaultValues: defaults,
  });

  // Re-sync defaults when initialValues changes (edit mode hydrates async).
  useEffect(() => {
    if (initialValues !== undefined) {
      form.reset(buildDefaults(initialValues));
    }
    // form.reset is stable; intentionally only depend on initialValues identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValues]);

  function handleSubmit(values: FormShape): void {
    // Defensively normalize: non-dropdown rows must have null options; empty
    // description string -> null.
    const normalizedRows: CustomFieldFormRow[] = values.customFields.map((f) =>
      f.dataType === 'dropdown'
        ? f
        : {
            ...f,
            dropdownOptions: null,
          },
    );
    const normalized: MachineTypeFormValues = {
      name: values.name,
      description:
        values.description === null || values.description === '' ? null : values.description,
      customFields: normalizedRows.map(toCustomField),
      isActive: values.isActive,
    };
    void onSubmit(normalized);
  }

  return (
    <form
      onSubmit={(e) => {
        void form.handleSubmit(handleSubmit)(e);
      }}
      className="space-y-6"
      noValidate
      data-testid={`mt-form-${mode}`}
    >
      {errorMessage != null && errorMessage !== '' && (
        <div
          role="alert"
          data-testid="mt-form-error"
          className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"
        >
          {errorMessage}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="mt-name">Name</Label>
        <Input
          id="mt-name"
          type="text"
          autoComplete="off"
          disabled={submitting}
          aria-invalid={form.formState.errors.name !== undefined}
          {...form.register('name')}
          data-testid="mt-form-name"
        />
        {form.formState.errors.name && (
          <p className="text-xs text-destructive" data-testid="mt-form-name-error">
            {form.formState.errors.name.message}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="mt-description">Description (optional)</Label>
        <Textarea
          id="mt-description"
          rows={3}
          disabled={submitting}
          aria-invalid={form.formState.errors.description !== undefined}
          {...form.register('description')}
          data-testid="mt-form-description"
        />
        {form.formState.errors.description && (
          <p className="text-xs text-destructive" data-testid="mt-form-description-error">
            {form.formState.errors.description.message}
          </p>
        )}
      </div>

      <CustomFieldsBuilder
        // The CustomFieldsBuilder only consumes the `customFields` slice of the
        // form; the cast narrows the parent form's full Control / setValue to
        // the subset shape the builder declares. RHF's runtime is field-name
        // based and doesn't care about the broader form schema.
        control={form.control as unknown as Control<{ customFields: CustomFieldFormRow[] }>}
        setValue={
          form.setValue as unknown as UseFormSetValue<{ customFields: CustomFieldFormRow[] }>
        }
        errors={form.formState.errors}
        disabled={submitting}
      />

      <div className="flex items-center gap-2">
        <input
          id="mt-active"
          type="checkbox"
          disabled={submitting}
          {...form.register('isActive')}
          data-testid="mt-form-active"
          className="h-4 w-4 rounded border-input"
        />
        <Label htmlFor="mt-active">Active</Label>
      </div>

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={submitting} data-testid="mt-form-submit">
          {submitting
            ? mode === 'create'
              ? 'Creating…'
              : 'Saving…'
            : mode === 'create'
              ? 'Create machine type'
              : 'Save changes'}
        </Button>
        <Button asChild variant="outline" type="button">
          <Link to="/machine-types" data-testid="mt-form-cancel">
            Cancel
          </Link>
        </Button>
      </div>
    </form>
  );
}
